
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Pet, Vaccine, Medication, MedicalVisit, PetPost } from '../types/pet';

interface PetContextType {
  pets: Pet[];
  addPet: (pet: Omit<Pet, 'id' | 'vaccines' | 'medications' | 'history' | 'posts'>) => void;
  updatePet: (id: string, updates: Partial<Pet>) => void;
  deletePet: (id: string) => void;
  addVaccine: (petId: string, vaccine: Omit<Vaccine, 'id'>) => void;
  addMedication: (petId: string, med: Omit<Medication, 'id'>) => void;
  addVisit: (petId: string, visit: Omit<MedicalVisit, 'id'>) => void;
  setPets: (pets: Pet[]) => void;
}

const PetContext = createContext<PetContextType | undefined>(undefined);

// Adding explicit props interface for PetProvider to handle children correctly
interface PetProviderProps {
  children?: ReactNode;
}

export const PetProvider: React.FC<PetProviderProps> = ({ children }) => {
  const [pets, setPets] = useState<Pet[]>([]);

  const addPet = (petData: any) => {
    const newPet: Pet = {
      ...petData,
      id: Math.random().toString(36).substr(2, 9),
      vaccines: [],
      medications: [],
      history: [],
      posts: []
    };
    setPets([...pets, newPet]);
  };

  const updatePet = (id: string, updates: Partial<Pet>) => {
    setPets(pets.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const deletePet = (id: string) => {
    setPets(pets.filter(p => p.id !== id));
  };

  const addVaccine = (petId: string, vaccine: any) => {
    setPets(pets.map(p => p.id === petId ? {
      ...p,
      vaccines: [...p.vaccines, { ...vaccine, id: Date.now().toString() }]
    } : p));
  };

  const addMedication = (petId: string, med: any) => {
    setPets(pets.map(p => p.id === petId ? {
      ...p,
      medications: [...p.medications, { ...med, id: Date.now().toString() }]
    } : p));
  };

  const addVisit = (petId: string, visit: any) => {
    setPets(pets.map(p => p.id === petId ? {
      ...p,
      history: [...p.history, { ...visit, id: Date.now().toString() }]
    } : p));
  };

  return (
    <PetContext.Provider value={{ pets, addPet, updatePet, deletePet, addVaccine, addMedication, addVisit, setPets }}>
      {children}
    </PetContext.Provider>
  );
};

export const usePets = () => {
  const context = useContext(PetContext);
  if (!context) throw new Error('usePets must be used within a PetProvider');
  return context;
};